/**
 * Utility to fix mock Firebase UIDs in existing database
 * Run this in the browser console to update users with mock UIDs
 */

import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

/**
 * Lists all users with mock Firebase UIDs (UIDs that start with 'firebase_uid_')
 */
export const listUsersWithMockUIDs = async () => {
  try {
    console.log('🔍 Searching for users with mock Firebase UIDs...');
    
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    const usersWithMockUIDs = [];
    snapshot.docs.forEach(doc => {
      const userData = doc.data();
      if (userData.firebase_uid && userData.firebase_uid.startsWith('firebase_uid_')) {
        usersWithMockUIDs.push({
          id: doc.id,
          ...userData
        });
      }
    });
    
    console.log(`📋 Found ${usersWithMockUIDs.length} users with mock Firebase UIDs:`);
    usersWithMockUIDs.forEach(user => {
      console.log(`  - ${user.name} (${user.email}) - ID: ${user.id} - Mock UID: ${user.firebase_uid}`);
    });
    
    return usersWithMockUIDs;
  } catch (error) {
    console.error('❌ Error listing users:', error);
    throw error;
  }
};

/**
 * Updates a specific user's Firebase UID
 * @param {string} userId - Document ID of the user
 * @param {string} newFirebaseUid - New Firebase UID to set
 */
export const updateUserFirebaseUid = async (userId, newFirebaseUid) => {
  try {
    console.log(`🔄 Updating user ${userId} with new Firebase UID: ${newFirebaseUid}`);
    
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      firebase_uid: newFirebaseUid,
      updated_at: new Date().toISOString()
    });
    
    console.log(`✅ Successfully updated user ${userId}`);
  } catch (error) {
    console.error(`❌ Error updating user ${userId}:`, error);
    throw error;
  }
};

/**
 * Interactive helper to update a user's Firebase UID
 * Call this function and follow the prompts
 */
export const interactiveUidUpdate = async () => {
  try {
    const users = await listUsersWithMockUIDs();
    
    if (users.length === 0) {
      console.log('✅ No users found with mock Firebase UIDs. All good!');
      return;
    }
    
    console.log('\n🛠️  To update a user, copy and run one of these commands:');
    console.log('     (Replace "YOUR_REAL_FIREBASE_UID" with the actual Firebase UID from login attempts)\n');
    
    users.forEach((user, index) => {
      console.log(`${index + 1}. For ${user.name} (${user.email}):`);
      console.log(`   updateUserFirebaseUid("${user.id}", "YOUR_REAL_FIREBASE_UID")\n`);
    });
    
    console.log('💡 Tips:');
    console.log('   - Get the real Firebase UID from the browser console when you try to login');
    console.log('   - Look for "AuthContext: Looking for user with Firebase UID: ..." in the logs');
    console.log('   - The real UID will look like: "A1B2C3D4E5F6G7H8I9J0K1L2M3N4O5P6"');
    
  } catch (error) {
    console.error('❌ Error in interactive update:', error);
  }
};

/**
 * Bulk update all users to remove mock UIDs (sets them to null)
 * Use this as a last resort - you'll need to manually set correct UIDs later
 */
export const clearAllMockUIDs = async () => {
  try {
    const users = await listUsersWithMockUIDs();
    
    if (users.length === 0) {
      console.log('✅ No users found with mock Firebase UIDs.');
      return;
    }
    
    console.log(`⚠️  About to clear Firebase UIDs for ${users.length} users...`);
    
    for (const user of users) {
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, {
        firebase_uid: null,
        updated_at: new Date().toISOString()
      });
      console.log(`🧹 Cleared UID for ${user.name} (${user.email})`);
    }
    
    console.log('✅ All mock Firebase UIDs cleared. Users will need to be re-linked on next login.');
  } catch (error) {
    console.error('❌ Error clearing mock UIDs:', error);
    throw error;
  }
};

// Make functions available globally for console use
if (typeof window !== 'undefined') {
  window.safelineUtils = {
    listUsersWithMockUIDs,
    updateUserFirebaseUid,
    interactiveUidUpdate,
    clearAllMockUIDs
  };
  
  console.log('🔧 SafeLine utilities loaded! Available commands:');
  console.log('   - safelineUtils.listUsersWithMockUIDs()');
  console.log('   - safelineUtils.interactiveUidUpdate()');
  console.log('   - safelineUtils.updateUserFirebaseUid(userId, newUid)');
  console.log('   - safelineUtils.clearAllMockUIDs()');
}

export default {
  listUsersWithMockUIDs,
  updateUserFirebaseUid,
  interactiveUidUpdate,
  clearAllMockUIDs
};